import React, { useEffect, useMemo, useState } from 'react';
import { GlassCard, CardHeader, Button, Icon, DataTable, Pagination, SearchInput, Tabs, Modal, ModalClose } from '../../campaign-os';
import { campaignService, getErrorMessage } from '../../../../services/apiService';
import { useShellToast } from '../useShellToast';

/**
 * "Fix email fields" — badly formatted vendor email fields with the proposed correction
 * (GET /campaigns/email-fixes), saved after review (POST /campaigns/email-fixes/apply) in the
 * clean "a@x, b@y" form. Changes vendor records only (each one audited); never sends mail.
 *
 * `vendorIds` narrows the list to one campaign's failed vendors.
 */
const num = (n) => Number(n || 0).toLocaleString('en-IN');
const PAGE_SIZE = 10;

const FIX_TABS = [
  { key: 'automatic', label: 'Clean automatically' },
  { key: 'confirm', label: 'Repaired — confirm' },
  { key: 'none', label: 'Nothing usable' },
];

export function EmailFixesPanel({ vendorIds = null, onApplied }) {
  const toast = useShellToast();
  const [scan, setScan] = useState({ data: null, loading: false, error: null });
  const [tab, setTab] = useState('automatic');
  const [selected, setSelected] = useState(() => new Set());
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);

  const scope = useMemo(() => (vendorIds ? new Set(vendorIds) : null), [vendorIds]);

  const load = async () => {
    setScan((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await campaignService.emailFixes();
      const all = res?.data ?? null;
      const keep = (list) => (scope ? (list || []).filter((r) => scope.has(r.vendor_id)) : list || []);
      setScan({
        data: all && { scanned: all.scanned, erp: all.erp, automatic: keep(all.automatic), confirm: keep(all.confirm), none: keep(all.none) },
        loading: false,
        error: null,
      });
      setSelected(new Set());
      setEdits({});
    } catch (err) {
      setScan((s) => ({ ...s, loading: false, error: getErrorMessage(err) || 'Could not scan the email fields' }));
    }
  };

  useEffect(() => setPage(1), [tab, search]);

  const d = scan.data;
  const all = d?.[tab] ?? [];
  const q = search.trim().toLowerCase();
  const visible = q ? all.filter((r) => [r.company_name, r.vendor_code, r.current].some((v) => String(v || '').toLowerCase().includes(q))) : all;
  const pageRows = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selectable = tab !== 'none';
  const valueOf = (r) => (edits[r.vendor_id] ?? r.proposed ?? '').trim();

  const allVisibleSelected = selectable && visible.length > 0 && visible.every((r) => selected.has(r.vendor_id));
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((r) => next.delete(r.vendor_id));
      else visible.forEach((r) => next.add(r.vendor_id));
      return next;
    });
  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const chosen = useMemo(() => {
    if (!d) return [];
    return [...d.automatic, ...d.confirm].filter((r) => selected.has(r.vendor_id)).map((r) => ({ ...r, value: valueOf(r) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, selected, edits]);

  const apply = async () => {
    if (!chosen.length || applying) return;
    setApplying(true);
    try {
      const res = await campaignService.applyEmailFixes(chosen.map((r) => ({ vendor_id: r.vendor_id, email: r.value })));
      const out = res?.data || {};
      const rejected = out.rejected?.length || 0;
      toast(`${num(out.updated)} vendor email${out.updated === 1 ? '' : 's'} corrected${rejected ? ` · ${num(rejected)} rejected` : ''}`, { tone: rejected ? 'warning' : 'success' });
      setConfirming(false);
      await load();
      onApplied?.();
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not save the corrections', { tone: 'error' });
    } finally {
      setApplying(false);
    }
  };

  const columns = [
    ...(selectable
      ? [
          {
            key: 'pick',
            header: <input type="checkbox" aria-label="Select all shown" checked={allVisibleSelected} onChange={toggleAll} className="h-4 w-4 accent-[#0da89b]" />,
            width: 36,
            render: (r) => <input type="checkbox" aria-label={`Select ${r.company_name}`} checked={selected.has(r.vendor_id)} onChange={() => toggle(r.vendor_id)} className="h-4 w-4 accent-[#0da89b]" />,
          },
        ]
      : []),
    {
      key: 'vendor',
      header: 'Vendor',
      render: (r) => (
        <div className="min-w-[150px] max-w-[220px]">
          <div className="truncate font-medium text-cos-ink" title={r.company_name}>{r.company_name}</div>
          <div className="mono text-2xs text-cos-ink-muted">{r.vendor_code || '—'} · {r.status}</div>
        </div>
      ),
    },
    { key: 'current', header: 'Email field now', render: (r) => <div className="mono max-w-[240px] break-all text-[12px] text-cos-ink">{r.current || '—'}</div> },
    {
      key: 'problems',
      header: 'What is wrong',
      render: (r) => (
        <div className="max-w-[260px] text-[12px] leading-[16px] text-cos-ink-secondary">
          {r.source === 'erp' && (
            <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-cos-teal-faint px-2 py-0.5 text-2xs font-semibold text-cos-teal-deep">
              <Icon name="database" className="text-[12px]" /> From the ERP
            </span>
          )}
          <div>{r.problems.join(' · ')}</div>
        </div>
      ),
    },
    ...(selectable
      ? [
          {
            key: 'proposed',
            header: tab === 'confirm' ? 'Proposed (editable)' : 'Will become',
            render: (r) =>
              tab === 'confirm' ? (
                <input
                  value={edits[r.vendor_id] ?? r.proposed ?? ''}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [r.vendor_id]: e.target.value }))}
                  className="mono h-8 w-[240px] rounded-lg border border-cos-line bg-white/70 px-2 text-[12px] text-cos-ink focus:border-cos-teal focus:outline-none"
                />
              ) : (
                <div className="mono max-w-[240px] break-all text-[12px] text-cos-teal-deep">{r.proposed}</div>
              ),
          },
        ]
      : []),
  ];

  return (
    <GlassCard padded={false}>
      <div className="flex flex-col gap-3 border-b border-cos-line-subtle px-5 py-4">
        <CardHeader
          className="!mb-0"
          title="Fix the email fields"
          icon={
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-cos-teal-faint text-cos-teal-deep">
              <Icon name="auto_fix_high" className="text-[18px]" weight={500} />
            </span>
          }
          subtitle={`${scope ? "This campaign's vendors without a delivery" : 'Every vendor'} whose email field is not one clean address. Saving changes the vendor record only (audited); no mail is sent.`}
          action={
            <Button size="sm" variant={d ? 'secondary' : 'primary'} icon={scan.loading ? 'progress_activity' : 'search'} onClick={load} disabled={scan.loading}>
              {scan.loading ? 'Scanning…' : d ? 'Scan again' : 'Scan the email fields'}
            </Button>
          }
        />
        {scan.error && <p className="rounded-xl border border-cos-status-coral/30 bg-cos-status-coral-bg px-3 py-2.5 text-[12px] text-cos-status-coral">{scan.error}</p>}
        {d && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Tabs size="sm" value={tab} onChange={setTab} items={FIX_TABS.map((t) => ({ ...t, count: num(d[t.key]?.length) }))} />
            <SearchInput value={search} onChange={setSearch} placeholder="Search vendor, code or email…" className="min-w-[200px] max-w-xs flex-1" />
          </div>
        )}
      </div>

      {!d && !scan.loading && (
        <p className="px-5 py-6 text-center text-[13px] text-cos-ink-muted">
          Scan to list each badly formatted field with its correction — e.g. <span className="mono">mtipune@gmail.com ; mtiaccpune@gmail.com</span> becomes two clean addresses.
        </p>
      )}

      {d && (
        <>
          <p className="px-5 pt-3 text-2xs text-cos-ink-muted">
            {tab === 'automatic' &&
              'Safe to save: several addresses in one field, a name or "mailto:" stuck to the address, stray punctuation, or a field cut short that the ERP still has whole. Every address is re-checked on save.'}
            {tab === 'confirm' &&
              'A repair to check: a misspelt domain (gamil.com → gmail.com), an address cut short and completed from the other one in the field, or the ERP’s address for a vendor with none or whose address bounced. Check each one, edit if needed, then tick it.'}
            {tab === 'none' && 'No usable address can be recovered from these fields. The vendor has to supply a new one.'}
            {d.erp === 'unavailable' && ' The ERP could not be reached, so its addresses are not used in this scan.'}
          </p>
          <div className="px-2">
            <DataTable columns={columns} rows={pageRows} rowKey={(r) => r.vendor_id} emptyLabel="Nothing in this list." />
          </div>
          <div className="flex flex-col gap-3 border-t border-cos-line-subtle px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
            {visible.length > PAGE_SIZE ? (
              <Pagination page={page} pageSize={PAGE_SIZE} total={visible.length} onPageChange={setPage} noun="vendors" />
            ) : (
              <span className="text-[13px] text-cos-ink-secondary">{num(visible.length)} vendors</span>
            )}
            {selectable && (
              <Button variant="primary" icon="save" disabled={!chosen.length} onClick={() => setConfirming(true)}>
                Save {num(chosen.length)} correction{chosen.length === 1 ? '' : 's'}
              </Button>
            )}
          </div>
        </>
      )}

      <Modal open={confirming} onClose={() => !applying && setConfirming(false)} size="lg" labelledBy="fix-title">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id="fix-title" className="text-[17px] font-bold tracking-tight text-cos-ink">Save {num(chosen.length)} email correction{chosen.length === 1 ? '' : 's'}?</h2>
              <p className="mt-1 text-[13px] leading-[19px] text-cos-ink-secondary">
                Each vendor&apos;s email field is replaced with the clean value below and the change is written to the vendor&apos;s audit log. Nothing is sent.
              </p>
            </div>
            <ModalClose onClose={() => !applying && setConfirming(false)} />
          </div>
          <div className="glass-inset mt-4 max-h-[340px] overflow-y-auto p-3">
            <table className="w-full text-[12px]">
              <tbody>
                {chosen.slice(0, 200).map((r) => (
                  <tr key={r.vendor_id} className="border-b border-cos-line-subtle last:border-0">
                    <td className="py-1.5 pr-3 align-top font-medium text-cos-ink">{r.company_name}</td>
                    <td className="mono py-1.5 pr-3 align-top text-cos-ink-muted line-through decoration-cos-status-coral/50">{r.current}</td>
                    <td className="mono py-1.5 align-top text-cos-teal-deep">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {chosen.length > 200 && <p className="pt-2 text-2xs text-cos-ink-muted">…and {num(chosen.length - 200)} more.</p>}
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={applying}>Cancel</Button>
            <Button variant="primary" icon="save" onClick={apply} disabled={applying}>{applying ? 'Saving…' : 'Save corrections'}</Button>
          </div>
        </div>
      </Modal>
    </GlassCard>
  );
}

export default EmailFixesPanel;
